import usePostStripePayment from '@/src/common/hooks/usePostStripePayment'
import { formatToCents } from '@/src/common/utilities/currencyFormatter'
import { theme } from '@/src/stitches.config'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { createContext, useContext, useEffect, useReducer } from 'react'

const stripePromise = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!
)

export const PaymentContext = createContext<any>(null)
export const PaymentUpdateContext = createContext<any>(null)

export const usePaymentState = () => useContext(PaymentContext)
export const useUpdatePaymentState = () => useContext(PaymentUpdateContext)

export const CheckoutPaymentProvider = ({
    children,
    paymentProcessor,
    cartSessionUuid,
    orderTotal,
    virtualLineItemEmails,
    hasPGPlusItem,
    isKYCAndNotRejected,
    totalLoadValue,
    numberOfCards,
    cartStateUuid,
    setShowLoader,
}: any) => {
    const [state, updateState] = useReducer(
        (current: any, changes: any) => ({ ...current, ...changes }),
        {
            loading: false,
            paymentProcessor,
            cartSessionUuid,
            paymentIntent: null,
            kountSessionId: null,
            orderTotal,
            virtualLineItemEmails,
            hasPGPlusItem,
            isKYCAndNotRejected,
            totalLoadValue,
            numberOfCards,
            paymentMessage: '',
            billingAddress: null,
            requiresCustomerUpdate: false,
            requiresSmsVerification: Boolean(
                Number(
                    process.env
                        .NEXT_PUBLIC_CHECKOUT_SMS_VERIFICATION_REQUIRED ?? 1
                )
            ),
        }
    )

    const { mutateAsync: postPaymentIntent } = usePostStripePayment()
    const { requiresCustomerUpdate } = state

    const ready =
        state.paymentProcessor &&
        (state.paymentProcessor !== 'stripe' || state.paymentIntent)

    const injectKount = () => {
        ;(function () {
            const zi = document.createElement('script')
            zi.src = String(process.env.NEXT_PUBLIC_KOUNT_DATA_COLLECTOR_URL)
            zi.id = 'kount-snippet'
            zi.onload = () => {
                // @ts-ignore
                const kountClient = new ka.ClientSDK()
                // @ts-ignore
                updateState({ kountSessionId: ka.sessionId })
                kountClient.autoLoadEvents()
            }
            const s = document.getElementsByTagName('script')[0]
            if (s) {
                // @ts-ignore
                s.parentNode.insertBefore(zi, s)
            }
        })()
    }
    useEffect(() => {
        injectKount()
    }, [])

    useEffect(() => {
        setShowLoader(!ready || state.loading)
    }, [ready, setShowLoader, state.loading])

    useEffect(() => {
        if (state.paymentProcessor !== 'stripe' || !state.cartSessionUuid) {
            return
        }

        if (
            state.paymentIntent?.amount !==
            formatToCents(parseFloat(orderTotal), 'USD')
        ) {
            postPaymentIntent(
                {
                    cart_session_uuid: cartSessionUuid,
                    payment_intent_id: state.paymentIntent?.id,
                    requires_customer_update: requiresCustomerUpdate,
                },
                {
                    onSuccess: (data) => {
                        updateState({
                            paymentIntent: data.stripe_payment_intent,
                        })
                    },
                    onError: () => {
                        alert(
                            'Unable to handle payment. Please try again or click Chat Now to get assistance from our GiftGuide.'
                        )
                    },
                }
            )
        }
    }, [paymentProcessor, orderTotal, cartSessionUuid, cartStateUuid])

    // updates list of virtual line item emails when items are removed from cart
    useEffect(() => {
        updateState({ virtualLineItemEmails: virtualLineItemEmails })
    }, [virtualLineItemEmails])

    return (
        <PaymentContext.Provider value={state}>
            <PaymentUpdateContext.Provider value={updateState}>
                {ready && (
                    <Elements
                        options={{
                            appearance: {
                                labels: 'floating',
                                variables: {
                                    colorIcon: '#6772e5',
                                    fontFamily: theme.fonts.default.value,
                                    fontSizeBase: '16px',
                                    fontSizeSm: '16px',
                                    colorDanger: theme.colors.error.value,
                                    fontSmooth: 'never',
                                },
                                theme: 'stripe',
                                rules: {
                                    '.Error': {
                                        paddingLeft: '10px',
                                    },
                                },
                            },
                            fonts: [
                                {
                                    family: 'Visby',
                                    src: `url(https://www.perfectgift.com/fonts/VisbyCF-DemiBold.woff2)`,
                                    weight: '500',
                                },
                            ],
                            clientSecret: state.paymentIntent?.client_secret,
                            // @ts-ignore
                            paymentMethodCreation: 'manual',
                        }}
                        stripe={stripePromise}
                    >
                        {children}
                    </Elements>
                )}
            </PaymentUpdateContext.Provider>
        </PaymentContext.Provider>
    )
}
